"use client";
import { LoaderComponent, PaperComponent, SliderComponent } from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  AMPLITUDE_CODES,
  CurrencyConverter,
  GET_PRODUCT_APPLICATION,
  MOENGAGE_CODES,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useLogoutHook from "@/lib/hooks/logoutHook";
import {
  useApplyLoanMutation,
  useCreateApplicationMutation,
  useGetApplicationQuery,
  useGetCustomerQuery,
  usePostUtmDetailsMutation,
  useProductDetailQuery,
} from "@/lib/redux";
import { setAmplitudeIdentifyProperties } from "@/lib/utils/amplitude.utils";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import styles from "../applyloan.module.css";
import { setMoEngageUserAttributes } from "@/lib/Moengage";
import { checkForRplProductRoute } from "@/lib/utils/rpl.utils";

function LeftCardApplyLoan({ amountRequired, tenureRequired, product }) {
  const searchParams = useSearchParams();
  const params = {
    utmSource: searchParams.get("utmSource"),
    utmMedium: searchParams.get("utmMedium"),
    utmTerm: searchParams.get("utmTerm"),
    utmAdsetid: searchParams.get("utmAdsetid"),
    utmCampaign: searchParams.get("utmCampaign"),
    utmNetwork: searchParams.get("utmNetwork"),
    utmMatchtype: searchParams.get("utmMatchtype"),
    utmDevice: searchParams.get("utmDevice"),
    utmPlacement: searchParams.get("utmPlacement"),
    utmContent: searchParams.get("utmContent"),
    utmAdposition: searchParams.get("utmAdposition"),
    utmLocation: searchParams.get("utmLocation"),
    utmSitelink: searchParams.get("utmSitelink"),
    utmAudience: searchParams.get("utmAudience"),
    utmPromotion: searchParams.get("utmPromotion"),
    utmPrice: searchParams.get("utmPrice"),
    utmCampaignid: searchParams.get("utmCampaignid"),
    gadSource: searchParams.get("gadSource"),
    gclid: searchParams.get("gclid") || searchParams.get("fbclid"),
    utmCampaigntype: searchParams.get("utmCampaigntype"),
  };
  const router = useRouter();
  const t = useTranslations("ApplyLoan");
  const [amount, setAmount] = useState(50000);
  const [tenure, setTenure] = useState(12);

  const { setActions } = useFooter();
  const {
    data: productData,
    isLoading: fetchingProductDetails,
    error: detailsFetchError,
  } = useProductDetailQuery({
    product: PRODUCT_HANDLER[product],
  });
  const {
    data: customerData,
    isLoading: fetchingCustomerData,
    isSuccess: customerDatafetched,
  } = useGetCustomerQuery("");
  const {
    data: applicationData,
    isLoading: fetchingApplications,
    error: applicationFetchError,
    isSuccess: applicationsFetched,
  } = useGetApplicationQuery("", {
    skip: !customerDatafetched,
    refetchOnFocus: true,
  });
  const [
    createApplication,
    { error: applicationCreationError, isLoading: creatingApplication },
  ] = useCreateApplicationMutation();
  const [
    applyLoan,
    { data: applyLoanData, isLoading: applyingForLoan, error: applyingError },
  ] = useApplyLoanMutation();

  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const [postUtmParams, { data: utmResponse, error: utmError }] =
    usePostUtmDetailsMutation();

  const isLoading =
    fetchingApplications ||
    creatingApplication ||
    applyingForLoan ||
    fetchingProductDetails ||
    fetchingCustomerData;
  const isError =
    applicationFetchError ||
    applicationCreationError ||
    applyingError ||
    detailsFetchError;

  useErrorHandling(isError);

  useEffect(() => {
    if (applicationsFetched && !productApplication?.appId) {
      createApplication({ product_type: PRODUCT_HANDLER[product] });
    }
  }, [applicationsFetched]);

  useEffect(() => {
    if (productApplication?.appId && customerData?.data) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.APPLY_LOAN_STARTED,
        ...productApplication,
        ...customerData?.data,
      });

      setAmplitudeIdentifyProperties({
        [AMPLITUDE_CODES.IDENTIFIERS.APP_ID]: productApplication?.appId,
        [AMPLITUDE_CODES.IDENTIFIERS.CUSTOMER_ID]: customerData?.data?.id,
      });
      setMoEngageUserAttributes({
        [MOENGAGE_CODES.USER_ATTRIBUTES.APP_ID]: productApplication?.appId,
        [MOENGAGE_CODES.USER_ATTRIBUTES.MOBILE_NUMBER]:
          customerData?.data?.phone,
      });
    }
    if (productApplication?.requestedLoanAmount) {
      setAmount(productApplication?.requestedLoanAmount);
    }
    if (productApplication?.requestedTenure) {
      setTenure(productApplication?.requestedTenure);
    }
    if (
      customerData?.data?.id &&
      productApplication?.appId &&
      params?.utmSource
    ) {
      postUtmParams({
        ...params,
        customerId: customerData?.data?.id,
        appId: productApplication.appId,
        utmInboundUrl: window?.location?.href,
        utmDevice: "web",
      });
    }
  }, [customerData, productApplication]);

  useEffect(() => {
    if (applyLoanData?.statusCode === 202) {
      router.push(
        STAGE_BASED_ROUTING(productApplication?.stage) ||
          `/${product}/identity-details`,
      );
    }
  }, [applyLoanData, productApplication]);

  useEffect(() => {
    const isRPL = checkForRplProductRoute(product);
    const stage = productApplication?.stage;
    if (
      stage &&
      (isRPL ||
        (stage !== STAGE.PAN &&
          stage !== STAGE.CKYC &&
          stage !== STAGE.PERSONAL_DETAILS &&
          stage !== STAGE.ADD_ADDRESS))
    ) {
      router.push(STAGE_BASED_ROUTING(stage));
    }
  }, [productApplication]);

  const ApplyLoan = useCallback(async () => {
    const productApplication = GET_PRODUCT_APPLICATION(
      PRODUCT_HANDLER[product],
      applicationData?.data,
    );
    let appId = productApplication?.appId;
    if (!appId) {
      const newApplication = await createApplication({
        product_type: PRODUCT_HANDLER[product],
      })?.unwrap();
      appId = newApplication?.data?.appId;
    }
    if (appId && amount && tenure) {
      applyLoan({
        appId: appId,
        requestedLoanAmount: amount.toString(),
        requestedTenure: tenure,
      });
    }
  }, [
    productApplication?.appId,
    applicationData?.data,
    tenure,
    amount,
    product,
  ]);
  useEffect(() => {
    setActions({
      rejectBtnLabel: "Back",
      onConfirm: ApplyLoan,
      confirmBtnLabel: productApplication?.requestedLoanAmount
        ? t("updateloan")
        : t("applyloan"),
      showReject: true,
    });
  }, [setActions, productApplication, ApplyLoan]);
  return (
    <Stack justifyContent={"space-between"} alignItems={"center"} flexGrow={1}>
      <Stack
        flexGrow={1}
        width={{ lg: "80%", md: "80%", xs: "100%" }}
        maxWidth={"900px"}
        mb={{ xs: 0, md: 8 }}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Typography color={WEB_COLORS.primary} fontWeight={700} variant="h5">
          {t("loandetails")}
        </Typography>

        <PaperComponent
          elevation={0}
          sx={{
            my: 2,
            px: { xs: 0, md: 3 },
            pb: { xs: 0, md: 3 },
            width: "100%",
            bgcolor: { md: WEB_COLORS.paleSkyBlue, xs: "none" },
            borderRadius: "12px",
          }}
        >
          <LoaderComponent open={isLoading} />
          <Typography
            variant="subtitle1"
            className={styles["amount-select-header"]}
            gutterBottom
            mt={1}
          >
            {t("amountrequired")}
          </Typography>
          <Stack gap={2}>
            {amountRequired && (
              <Box
                sx={{
                  bgcolor: WEB_COLORS.lightSkyBlue,
                  py: 2,
                  px: 3,
                  borderRadius: 2,
                }}
              >
                <Stack
                  direction={"row"}
                  alignItems="center"
                  justifyContent="space-between"
                  color={WEB_COLORS.primary}
                >
                  <Typography className={styles["amount-label"]}>
                    {t("loanamount")}
                  </Typography>
                  <Typography className={styles["amount-view"]}>
                    {CurrencyConverter(amount)}
                  </Typography>
                </Stack>
                <Stack className={styles["slider-container"]}>
                  <SliderComponent
                    min={productData?.minLoanAmount}
                    max={productData?.maxLoanAmount}
                    value={amount}
                    step={10000}
                    amountRequired={amountRequired}
                    onChange={(_, newValue) => setAmount(newValue)}
                  />
                </Stack>
              </Box>
            )}
            {tenureRequired && (
              <Box
                sx={{
                  bgcolor: WEB_COLORS.lightSkyBlue,
                  py: 2,
                  px: 3,
                  borderRadius: 2,
                }}
              >
                <Stack
                  direction={"row"}
                  alignItems="center"
                  justifyContent="space-between"
                  color={WEB_COLORS.primary}
                >
                  <Typography className={styles["amount-label"]}>
                    {t("tenure")}
                  </Typography>
                  <Typography className={styles["amount-view"]}>
                    {tenure} {t("months")}
                  </Typography>
                </Stack>

                <Stack className={styles["slider-container"]}>
                  <SliderComponent
                    minTenure={12}
                    maxTenure={36}
                    tenure={tenure}
                    tenureRequired={tenureRequired}
                    handleTenureChange={(_, newValue) => setTenure(newValue)}
                    showMinMax={false}
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        </PaperComponent>
      </Stack>
    </Stack>
  );
}

export default LeftCardApplyLoan;
